/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { axiosInstance } from "@app/axios/axiosIns";

interface OtherField {
  model: string;
  label: string;
  value: string;
}

interface OtherFieldState {
  isLoading: boolean;
  formLoader: boolean;
  allFields: OtherField[];
  currentField: OtherField | null;
  error: string | null;
}

const initialState: OtherFieldState = {
  isLoading: false,
  formLoader: false,
  allFields: [],
  currentField: null,
  error: null,
};

export const addOtherField = createAsyncThunk<
  OtherField,
  Partial<OtherField>,
  { rejectValue: string }
>("otherField/add", async (data, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.post("/other-field", data);
    return res.data;
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error.message);
  }
});

export const getAllOtherFields = createAsyncThunk<
  OtherField[],
  void,
  { rejectValue: string }
>("otherField/all", async (_, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.get("/other-field");
    return res.data;
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error.message);
  }
});

export const getOtherFieldByModel = createAsyncThunk<
  OtherField,
  string,
  { rejectValue: string }
>("otherField/getByModel", async (model, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.get(`/other-field/${model}`);
    return res.data;
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error.message);
  }
});

export const updateOtherField = createAsyncThunk<
  OtherField,
  { model: string; data: Partial<OtherField> },
  { rejectValue: string }
>("otherField/update", async ({ model, data }, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.patch(`/other-field/${model}`, data);
    return res.data;
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error.message);
  }
});

const otherFieldSlice = createSlice({
  name: "otherField",
  initialState,
  reducers: {
    resetOtherFieldState: () => initialState,
    clearOtherFieldError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Add
      .addCase(addOtherField.pending, (state) => {
        state.formLoader = true;
      })
      .addCase(addOtherField.fulfilled, (state, action) => {
        state.formLoader = false;
        state.currentField = action.payload;
      })
      .addCase(addOtherField.rejected, (state, action) => {
        state.formLoader = false;
        state.error = action.payload || null;
      })

      // Get All
      .addCase(getAllOtherFields.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllOtherFields.fulfilled, (state, action) => {
        state.isLoading = false;
        state.allFields = action.payload;
      })
      .addCase(getAllOtherFields.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || null;
      })

      // Get by model
      .addCase(getOtherFieldByModel.pending, (state) => {
        state.isLoading = true;
        state.currentField = null;
      })
      .addCase(getOtherFieldByModel.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentField = action.payload;
      })
      .addCase(getOtherFieldByModel.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || null;
      })

      // Update
      .addCase(updateOtherField.pending, (state) => {
        state.formLoader = true;
      })
      .addCase(updateOtherField.fulfilled, (state, action) => {
        state.formLoader = false;
        state.currentField = action.payload;
      })
      .addCase(updateOtherField.rejected, (state, action) => {
        state.formLoader = false;
        state.error = action.payload || null;
      });
  },
});

export const { resetOtherFieldState, clearOtherFieldError } =
  otherFieldSlice.actions;
export default otherFieldSlice.reducer;
